import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Container } from "@mui/material";
import HorizontalScrollRow from "../../components/Accessories/HorizontalScrollRow";
import AccessoryItemCard from "./AccessoryItemCard";
import api from "../../services/api";

const AccessoriesPage = () => {
  const [accessoriesBySubcategory, setAccessoriesBySubcategory] = useState({});
  const { hash } = useLocation();

  useEffect(() => {
    document.title = "Accessories";
  }, []);

  useEffect(() => {
    const fetchAccessories = async () => {
      try {
        const res = await api.get("/accessories");
        const grouped = res.data.reduce((acc, item) => {
          const groupKey = item.subcategory || "Other";
          if (!acc[groupKey]) acc[groupKey] = [];
          acc[groupKey].push(item);
          return acc;
        }, {});
        setAccessoriesBySubcategory(grouped);
      } catch (err) {
        console.error("Failed to fetch accessories", err);
      }
    };

    fetchAccessories();
  }, []);

  const subcategoryPriority = ["candle", "cakeTopper", "greetingCard"];

  const sortedEntries = Object.entries(accessoriesBySubcategory).sort(
    ([a], [b]) => {
      const indexA = subcategoryPriority.indexOf(a);
      const indexB = subcategoryPriority.indexOf(b);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      } else if (indexA !== -1) {
        return -1;
      } else if (indexB !== -1) {
        return 1;
      } else {
        return a.localeCompare(b);
      }
    }
  );

  const formatSubcategoryId = (subcategory) =>
    subcategory
      .replace(/([a-z])([A-Z])/g, "$1-$2") // camelCase → dash-separated
      .toLowerCase() + "s"; // plural

  const formatSubcategoryTitle = (subcategory) =>
    subcategory.replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase() + "S";

  useEffect(() => {
    if (hash && sortedEntries.length > 0) {
      const sectionId = hash.replace("#", "");
      requestAnimationFrame(() => {
        const sectionEl = document.getElementById(sectionId);
        if (sectionEl) {
          sectionEl.scrollIntoView({ behavior: "smooth" });
        }
      });
    }
  }, [hash, sortedEntries]);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          height: 420,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          overflow: "hidden",
        }}>
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backgroundImage: `url('/images/accessories-banner.png')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 1,
          }}
        />
        <Typography
          variant="h1"
          fontWeight="bold"
          color="common.white"
          sx={{ zIndex: 2, px: 2 }}>
          Accessories to Elevate Your Celebration
        </Typography>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ mt: 5, mb: 10 }}>
        {sortedEntries.map(([subcategory, items]) => {
          const sectionId = formatSubcategoryId(subcategory);
          const formattedTitle = formatSubcategoryTitle(subcategory);

          return (
            <section key={subcategory} id={sectionId}>
              <HorizontalScrollRow
                title={formattedTitle}
                items={items}
                renderItem={(item) => (
                  <AccessoryItemCard key={item._id} item={item} />
                )}
              />
            </section>
          );
        })}
      </Container>
    </Box>
  );
};

export default AccessoriesPage;
